import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import { db } from "services/firebase";
import { useStripe } from "@stripe/react-stripe-js";
import {
  Alert,
  AlertTitle,
  Box,
  Typography,
  Stack,
  CircularProgress,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  Card,
} from "@mui/material";
import PricingControls from "./PricingControls";
import { LoadingButton } from "@mui/lab";
import { CheckCircle } from "@mui/icons-material";
import { useAuth } from "hooks/useAuth";

const Pricing = ({ uid, role, subscription }) => {
  const navigate = useNavigate();
  const stripe = useStripe();
  const {
    hasValidSubscription,
    canStartTrial,
    hasActiveTrial,
    trialExpired,
    userData,
  } = useAuth();
  const [trialLoading, setTrialLoading] = useState(null);
  const [prices, setPrices] = useState({});
  const [selectedInterval, setSelectedInterval] = useState("monthly");

  // All the existing Firebase logic stays here...
  const getPlans = async () => {
    if (role) {
      try {
        const plansSnapshot = await db
          .collection("plans")
          .where("active", "==", true)
          .where("role", "==", role)
          .get();

        const pricePromises = plansSnapshot.docs.map(async (doc) => {
          const productData = doc.data();

          const pricesSnapshot = await db
            .collection("plans")
            .doc(doc.id)
            .collection("prices")
            .where("active", "==", true)
            .get();

          const priceList = {};

          pricesSnapshot.forEach((price) => {
            const priceData = price.data();

            const roundNumber =
              String(priceData.unit_amount).slice(-2) === "00";
            const formattedAmount = roundNumber
              ? priceData.unit_amount / 100
              : (priceData.unit_amount / 100).toFixed(2);

            const newPrice = {
              id: price.id,
              role: productData.role,
              price: `$${formattedAmount}`,
              interval: priceData.interval || "day",
              type: priceData.type || "recurring",
            };

            if (priceData.description) {
              const [tier, interval] = priceData.description.split("_");
              if (tier && interval) {
                // Standard format: "medium_monthly"
                if (!priceList[tier]) {
                  priceList[tier] = {};
                }
                priceList[tier][interval] = newPrice;
              } else if (
                priceData.description === "monthly" ||
                priceData.description === "yearly"
              ) {
                // Facility format: just "monthly" or "yearly"
                if (!priceList["facility"]) {
                  priceList["facility"] = {};
                }
                priceList["facility"][priceData.description] = newPrice;
              }
            } else {
              let key =
                priceData.type === "one_time" ? "day" : priceData.interval;
              priceList[key] = newPrice;
            }
          });

          return priceList;
        });

        const allPriceLists = await Promise.all(pricePromises);
        const mergedPrices = allPriceLists.reduce(
          (acc, curr) => ({ ...acc, ...curr }),
          {}
        );

        setPrices(mergedPrices);
      } catch (error) {
        console.error("Error fetching plans:", error);
      }
    }
  };

  useEffect(() => {
    if (role) {
      getPlans();
    }
  }, [role]);

  const startTrial = async (planId = null) => {
    setTrialLoading(planId);
    try {
      const startTrialFunction = firebase
        .functions()
        .httpsCallable("startTrial");
      const result = await startTrialFunction();
      if (result.data.success) {
        console.log("Trial started successfully");
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Error starting trial:", error);
      alert(`Error starting trial: ${error.message}`);
      setTrialLoading(null);
    }
  };

  return (
    <>
      {trialExpired && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Trial Expired</AlertTitle>
          Your 24-hour trial has ended. Choose a plan below to get unlimited
          access to Medical SuperIntelligence.
        </Alert>
      )}

      {canStartTrial && (
        <Card
          sx={{
            textAlign: "center",
            py: 4,
            px: 2,
            my: 4,
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: "bold" }}>
            Try Medical SuperIntelligence Free for 24 Hours.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            No credit card required. Get full access to Medical
            SuperIntelligence for 24 hours.
          </Typography>
          <LoadingButton
            loading={trialLoading === "startTrial"}
            disabled={trialLoading !== null}
            variant="contained"
            size="large"
            onClick={() => startTrial("startTrial")}
            sx={{ px: 4, py: 1.5 }}
          >
            Start Free Trial
          </LoadingButton>
        </Card>
      )}

      <PricingControls
        prices={prices}
        selectedInterval={selectedInterval}
        onIntervalChange={setSelectedInterval}
        canStartTrial={canStartTrial}
        startTrial={startTrial}
        trialLoading={trialLoading}
        subscription={subscription}
        role={role}
        trialExpired={trialExpired}
      />
    </>
  );
};

export default Pricing;
